/**
 * The two balances a spot order can spend: the base token (a SELL) and the
 * quote token (a BUY).
 *
 * `amount` is `null` when the balance is NOT KNOWN — the account has not
 * loaded, or its collateral row carries no figure — and `"0"` only when the
 * account is loaded and simply holds none of that token. The two used to be the
 * same "0", which told a trader whose balance was still loading that they had
 * nothing, and let the size field refuse every order as "insufficient".
 */
import { getAssetShortName } from "../../../utils/asset/assets";
import { AssetResponse } from "../../../utils/asset";
import { floorToDecimalsString } from "../../../utils/decimalString";
import { floorSizeForDisplay } from "../../../utils/format";

export interface SpotTokenBalance {
  amount: string | null;
  symbol: string;
}

export interface SpotTokenBalances {
  base: SpotTokenBalance;
  quote: SpotTokenBalance;
}

interface CollateralLike {
  collateral_asset?: { asset_id?: string | null; asset_address?: string | null } | null;
  available_balance?: string | number | null;
  balance?: string | number | null;
}

interface SpotMarketLike {
  underlying_asset?: string;
  base_currency?: string;
  quote_currency?: string;
}

const figure = (value: string | number | null | undefined): string | null =>
  value === null || value === undefined || value === "" ? null : String(value);

/**
 * Match collaterals by address first, then by asset id (the API's `symbol` is
 * null). `collaterals` undefined means the account has not loaded: unknown.
 */
export const resolveSpotTokenBalances = (
  collaterals: CollateralLike[] | null | undefined,
  market: SpotMarketLike | undefined
): SpotTokenBalances => {
  const baseAsset = market?.underlying_asset;
  const baseSymbol =
    getAssetShortName(baseAsset as AssetResponse) || String(baseAsset || "");
  if (!collaterals) {
    return {
      base: { amount: null, symbol: baseSymbol },
      quote: { amount: null, symbol: "USDC" },
    };
  }
  const baseShort = getAssetShortName(baseAsset as AssetResponse)?.toLowerCase();
  const baseAddr = market?.base_currency?.toLowerCase();
  const quoteAddr = market?.quote_currency?.toLowerCase();
  const pick = (idMatches: (string | undefined)[], addr?: string) =>
    collaterals.find((c) => {
      const id = (c.collateral_asset?.asset_id || "").toLowerCase();
      const a = (c.collateral_asset?.asset_address || "").toLowerCase();
      return (
        (addr && a === addr) ||
        idMatches.some((m) => !!m && (id === m || id.includes(m)))
      );
    });
  // A token the loaded account has no row for is a zero balance, not an unknown one.
  const amountOf = (row: CollateralLike | undefined): string | null =>
    row === undefined ? "0" : figure(row.available_balance) ?? figure(row.balance);

  return {
    base: {
      amount: amountOf(pick([baseAsset?.toLowerCase(), baseShort], baseAddr)),
      symbol: baseSymbol,
    },
    quote: { amount: amountOf(pick(["usdc"], quoteAddr)), symbol: "USDC" },
  };
};

/**
 * "12.34 USDC" / "0.00000148 BTC", floored so the figure shown is never more
 * than the trader holds; "— BTC" when the balance is not known.
 */
export const formatSpotAvailable = (
  balance: SpotTokenBalance,
  amountStep: number,
  amountPrecision: number
): string => {
  if (balance.amount === null) return `— ${balance.symbol}`;
  const shown =
    balance.symbol === "USDC"
      ? floorToDecimalsString(balance.amount, 2)
      : // Floored at the step's precision: `toFixed(7)` showed a 0.00000148 BTC
        // balance as 0.0000015, which traders copied into the size field and
        // were rejected for.
        floorSizeForDisplay(balance.amount, amountStep, amountPrecision);
  return `${shown} ${balance.symbol}`;
};
